import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Nav } from "@/components/layout/Nav";
import { Footer } from "@/components/layout/Footer";
import { PageSEO } from "@/components/seo/PageSEO";
import { getProductSlug, getProductTypeLabel, SITE_URL } from "@/utils/seo";
import { API, BACKEND_URL } from "@/config";
import { ArrowRight } from "lucide-react";

const SITE = SITE_URL;

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Baby Gear Rentals", item: `${SITE}/gear` },
  ],
};

const imgSrc = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BACKEND_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

export default function BabyGear() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((r) => {
        const gear = (r.data.products || []).filter(
          (p) => p && p.category === "baby-gear" && p.active !== false && p.website_visible !== false && !p.id?.startsWith("test-")
        );
        setProducts(gear);
      })
      .finally(() => setLoading(false));
  }, []);

  // Derive unique subcategories from actual product data
  const subcats = [...new Set(products.map((p) => p.subcategory).filter(Boolean))];
  const displayed =
    filter === "all" ? products : products.filter((p) => p.subcategory === filter);

  return (
    <>
      <PageSEO
        title="Baby Gear Rentals Near Disney World & Orlando | Enchanted Gear Rentals"
        description="Rent cribs, high chairs, Pack 'n Plays and more near Walt Disney World and Orlando. All hospital-grade sanitized, free delivery to Disney resorts, Universal Orlando and area hotels."
        canonical={`${SITE}/gear`}
        structuredData={breadcrumbSchema}
      />
      <Nav />
      <main className="min-h-screen" style={{ background: "var(--color-bg, #040A0F)" }}>
        {/* Hero */}
        <section
          className="pt-28 pb-12 text-center"
          style={{ background: "linear-gradient(180deg, #060f18 0%, #040A0F 100%)" }}
        >
          <div className="container max-w-3xl">
            <nav aria-label="Breadcrumb" className="text-xs text-[#B9C8D5] mb-4">
              <Link to="/" className="hover:text-[#FFD166]">Home</Link>
              <span className="mx-2 opacity-40">/</span>
              <span className="text-[#FFD166]">Baby Gear Rentals</span>
            </nav>
            <h1 className="font-serif text-4xl md:text-5xl text-[#F6F1E4] mb-4">
              Baby Gear Rentals Near Disney World
            </h1>
            <p className="text-[#B9C8D5] text-lg max-w-2xl mx-auto">
              Travel light — rent the essentials your little one needs. Cribs, high chairs, Pack 'n
              Plays and more, all hospital-grade sanitized and delivered free to your resort, hotel
              or vacation home.
            </p>
            <p className="mt-3 text-sm text-[#B9C8D5]">
              From{" "}
              <span className="text-[#FFD166] font-semibold">$10/day</span> · Free delivery ·
              Enchanted Gear Rentals is not affiliated with The Walt Disney Company.
            </p>
          </div>
        </section>

        {/* Subcategory filter tabs — only show when there are multiple subcategories */}
        {subcats.length > 1 && (
          <div className="container pt-8 pb-4">
            <div className="flex gap-3 justify-center flex-wrap">
              {[
                { key: "all", label: "All Gear", count: products.length },
                ...subcats.map((s) => ({
                  key: s,
                  label: s,
                  count: products.filter((p) => p.subcategory === s).length,
                })),
              ].map(({ key, label, count }) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  data-testid={`gear-filter-${key.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium border transition-all ${
                    filter === key
                      ? "bg-[#FFD166] text-[#040A0F] border-[#FFD166]"
                      : "border-[#FFD166]/30 text-[#B9C8D5] hover:border-[#FFD166]/70"
                  }`}
                >
                  {label}{" "}
                  <span className="opacity-60 text-xs">({count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Product grid */}
        <section className="container pb-20 pt-4">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl bg-[#0A1825] border border-[#FFD166]/10 animate-pulse h-80"
                />
              ))}
            </div>
          ) : displayed.length === 0 ? (
            <p className="text-center text-[#B9C8D5] py-20">
              No items available in this category right now — check back soon.
            </p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gear-grid">
              {displayed.map((p) => {
                const slug = getProductSlug(p);
                const typeLabel = getProductTypeLabel(p);
                const img = imgSrc(p.image);
                return (
                  <Link
                    key={p.id}
                    to={`/strollers/${slug}`}
                    className="group rounded-2xl bg-[#0A1825] border border-[#FFD166]/10 hover:border-[#FFD166]/40 overflow-hidden transition-all hover:-translate-y-1"
                    data-testid={`gear-card-${p.id}`}
                  >
                    <div className="aspect-[4/3] bg-[#060f18] overflow-hidden">
                      {img ? (
                        <img
                          src={img}
                          alt={`${p.name} rental near Walt Disney World`}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                          width="400"
                          height="300"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#FFD166]/20 text-5xl">
                          &#9885;
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] uppercase tracking-widest text-[#B9C8D5] border border-[#FFD166]/20 px-2 py-0.5 rounded-full">
                          {typeLabel}
                        </span>
                        {p.min_age && (
                          <span className="text-[10px] text-[#B9C8D5]">{p.min_age}</span>
                        )}
                      </div>
                      <h2 className="font-serif text-xl text-[#F6F1E4] group-hover:text-[#FFD166] transition-colors">
                        {p.name}
                      </h2>
                      <p className="text-sm text-[#B9C8D5] mt-1 line-clamp-2">
                        {p.tagline || p.description?.slice(0, 80)}
                      </p>
                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-[#FFD166] font-semibold">
                          From ${p.tier_1_3}/day
                        </span>
                        <span className="text-xs text-[#B9C8D5] flex items-center gap-1">
                          View details <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="container pb-20 text-center">
          <div className="max-w-xl mx-auto rounded-2xl bg-[#0A1825] border border-[#FFD166]/20 p-8">
            <h2 className="font-serif text-2xl text-[#F6F1E4] mb-3">
              Ready to book your gear?
            </h2>
            <p className="text-[#B9C8D5] mb-6">
              Select your dates and delivery location to check availability and confirm pricing in
              real time.
            </p>
            <Link to="/#trip-setup">
              <button className="btn-primary" data-testid="gear-book-cta">
                Check Availability <ArrowRight className="inline w-4 h-4 ml-1" />
              </button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
